/* ===============================================================
   GLOBAL UTILITIES & ACCESSIBILITY
   =============================================================== */

/**
 * Returns all focusable elements inside a container, filtering out
 * - disabled / inert
 * - negative tabindex
 * - hidden (display:none, visibility:hidden, zero rect)
 * Matches keyboard-focusable controls only.
 */
function getFocusableElements(container) {
  if (!container) return [];
  const selectors = [
    'a[href]:not([tabindex^="-"]):not([inert])',
    'area[href]:not([tabindex^="-"]):not([inert])',
    'button:enabled:not([tabindex^="-"]):not([inert])',
    'select:enabled:not([tabindex^="-"]):not([inert])',
    'textarea:enabled:not([tabindex^="-"]):not([inert])',
    'input:not([type="hidden"]):enabled:not([tabindex^="-"]):not([inert])',
    'summary:not([tabindex^="-"]):not([inert])',
    'iframe:not([tabindex^="-"]):not([inert])',
    'object:not([tabindex^="-"]):not([inert])',
    'embed:not([tabindex^="-"]):not([inert])',
    '[contenteditable]:not([tabindex^="-"]):not([inert])',
    '[tabindex]:not([tabindex^="-"]):not([inert])'
  ];
  const all = Array.from(container.querySelectorAll(selectors.join(',')));

  return all.filter((el) => {
    if (!container.contains(el)) return false;
    if (el.hasAttribute('disabled') || el.getAttribute('aria-disabled') === 'true') return false;
    const ti = el.getAttribute('tabindex');
    if (ti !== null && Number(ti) < 0) return false;
    const details = el.closest('details:not([open])');
    if (details && !details.contains(el.closest('summary'))) return false;
    const style = window.getComputedStyle(el);
    if (style.visibility === 'hidden' || style.display === 'none') return false;
    if (!el.getClientRects().length) return false;
    return true;
  });
}


const trapFocusHandlers = {};

/**
 * Traps focus within a container. If elementToFocus provided, focus it.
 * Falls back gracefully if no focusable elements are found.
 */
function trapFocus(container, elementToFocus = null) {
  if (!container) return;
  removeTrapFocus();

  const focusables = getFocusableElements(container);
  const first = focusables[0] || container;
  const last = focusables[focusables.length - 1] || container;

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;

    if (focusables.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }

    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
      return;
    }

    if ((event.target === first || event.target === container) && event.shiftKey) {
      event.preventDefault();
      last.focus();
      return;
    }
  };

  trapFocusHandlers.focusin = (event) => {
    if (!container.contains(event.target)) {
      (first || container).focus();
    }
  };

  trapFocusHandlers.focusout = () => {};

  document.addEventListener('keydown', trapFocusHandlers.keydown);
  document.addEventListener('focusin', trapFocusHandlers.focusin);
  document.addEventListener('focusout', trapFocusHandlers.focusout);

  const targetToFocus = elementToFocus || first || container;
  if (typeof targetToFocus.focus === 'function') {
    targetToFocus.focus();
    if (
      targetToFocus.tagName === 'INPUT' &&
      ['search', 'text', 'email', 'url', 'tel', 'number'].includes(targetToFocus.type) &&
      targetToFocus.value
    ) {
      try { targetToFocus.setSelectionRange(0, targetToFocus.value.length); } catch(e) {}
    }
  }
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('keydown', trapFocusHandlers.keydown);
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);

  if (elementToFocus && typeof elementToFocus.focus === 'function') {
    elementToFocus.focus();
  }
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;
  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement?.setAttribute('aria-expanded', 'false');
  summaryElement?.focus();
}

/* ===============================================================
   SECTION ID HELPER
   =============================================================== */
class SectionId {
  static #separator = '__';
  static parseId(qualifiedSectionId) {
    return (qualifiedSectionId || '').split(SectionId.#separator)[0];
  }
  static parseSectionName(qualifiedSectionId) {
    return (qualifiedSectionId || '').split(SectionId.#separator)[1];
  }
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

/* ===============================================================
   HTML UPDATE UTILITY
   =============================================================== */
class HTMLUpdateUtility {
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    if (!oldNode || !newContent) return;
    preProcessCallbacks?.forEach((cb) => { try { cb(newContent); } catch(e){} });

    const wrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(wrapper, newContent.outerHTML);
    const newNode = wrapper.firstChild;

    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((el) => {
      if (el.id) el.id = `${el.id}-${uniqueKey}`;
      if (el.form) {
        const fid = el.form.getAttribute('id');
        if (fid) el.setAttribute('form', `${fid}-${uniqueKey}`);
      }
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((cb) => { try { cb(newNode); } catch(e){} });

    setTimeout(() => oldNode.remove(), 500);
  }

  static setInnerHTML(element, html) {
    if (!element) return;
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScript) => {
      const s = document.createElement('script');
      for (const attr of Array.from(oldScript.attributes)) s.setAttribute(attr.name, attr.value);
      s.appendChild(document.createTextNode(oldScript.innerHTML));
      oldScript.parentNode.replaceChild(s, oldScript);
    });
  }
}

/* ===============================================================
   SUMMARY/DETAILS A11Y
   =============================================================== */
document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));
  if (summary.nextElementSibling?.id) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }
  summary.addEventListener('click', (event) => {
    const isOpen = event.currentTarget.closest('details')?.hasAttribute('open');

    event.currentTarget.setAttribute('aria-expanded', String(!isOpen));
  });
  if (!summary.closest('header-drawer, menu-drawer')) {
    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
  }
});

/* ===============================================================
   :focus-visible Polyfill (fallback only)
   =============================================================== */
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP','ARROWDOWN','ARROWLEFT','ARROWRIGHT','TAB','ENTER','SPACE','ESCAPE','HOME','END','PAGEUP','PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) mouseClick = false;
  });
  window.addEventListener('mousedown', () => { mouseClick = true; });
  window.addEventListener('focus', () => {
    if (currentFocusedElement) currentFocusedElement.classList.remove('focused');
    if (mouseClick) return;
    currentFocusedElement = document.activeElement;
    currentFocusedElement?.classList.add('focused');
  }, true);
}

/* ===============================================================
   MEDIA HELPERS
   =============================================================== */
function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    try { video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*'); } catch(e){}
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    try { video.contentWindow.postMessage('{"method":"pause"}', '*'); } catch(e){}
  });
  document.querySelectorAll('video').forEach((video) => { try { video.pause(); } catch(e){} });
  document.querySelectorAll('product-model').forEach((model) => {
    try { if (model.modelViewerUI) model.modelViewerUI.pause(); } catch(e){}
  });
}
window.pauseAllMedia = pauseAllMedia;



/**
 * Debounce avancé
 * - leading: exécute à l'entrée
 * - trailing: exécute à la fin (par défaut)
 * - maxWait: garantit une exécution au plus tard toutes les X ms
 * Retourne une fonction décorée avec .cancel() et .flush()
 */
function debounce(fn, wait = 200, opts = {}) {
  const { leading = false, trailing = true, maxWait } = opts;

  let timerId = null;
  let lastInvokeTime = 0;
  let lastCallTime = 0;
  let lastArgs;
  let lastThis;
  let result;

  function invoke(time) {
    lastInvokeTime = time;
    const r = fn.apply(lastThis, lastArgs);
    result = r;
    lastArgs = lastThis = null;
    return r;
  }

  function shouldInvoke(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;

    return (
      lastCallTime === 0 || // premier appel
      timeSinceLastCall >= wait || // délai écoulé
      timeSinceLastCall < 0 || // horloge ajustée
      (typeof maxWait === 'number' && timeSinceLastInvoke >= maxWait) // maxWait atteint
    );
  }

  function startTimer(callback, ms) {
    return setTimeout(callback, ms);
  }

  function remainingWait(time) {
    const timeSinceLastCall = time - lastCallTime;
    const timeSinceLastInvoke = time - lastInvokeTime;
    const timeWaiting = wait - timeSinceLastCall;
    return (typeof maxWait === 'number')
      ? Math.min(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function trailingEdge(time) {
    timerId = null;
    if (trailing && lastArgs) {
      return invoke(time);
    }
    lastArgs = lastThis = null;
    return result;
  }

  function timerExpired() {
    const time = Date.now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // sinon, replanifie
    timerId = startTimer(timerExpired, remainingWait(time));
  }

  function leadingEdge(time) {
    lastInvokeTime = time;
    timerId = startTimer(timerExpired, wait);
    if (leading) {
      return invoke(time);
    }
    return result;
  }

  function debounced(...args) {
    const time = Date.now();
    const isInvoking = shouldInvoke(time);

    lastArgs = args;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === null) {
        return leadingEdge(time);
      }
      if (typeof maxWait === 'number') {
        // réinitialise le timer pour la queue
        clearTimeout(timerId);
        timerId = startTimer(timerExpired, wait);
        return invoke(time);
      }
    }
    if (timerId === null) {
      timerId = startTimer(timerExpired, wait);
    }
    return result;
  }

  debounced.cancel = function () {
    if (timerId !== null) {
      clearTimeout(timerId);
    }
    timerId = lastArgs = lastThis = null;
    lastCallTime = lastInvokeTime = 0;
  };

  debounced.flush = function () {
    if (timerId === null) return result;
    return trailingEdge(Date.now());
  };

  return debounced;
}
function throttle(fn, wait = 200, { leading = true, trailing = true } = {}) {
  return debounce(fn, wait, { leading, trailing, maxWait: wait });
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/* ===============================================================
   SHOPIFY COMMON
   =============================================================== */
if (typeof window.Shopify === 'undefined') window.Shopify = {};

Shopify.bind = function (fn, scope) {
  return function () { return fn.apply(scope, arguments); };
};
Shopify.setSelectorByValue = function (selector, value) {
  for (let i = 0; i < selector.options.length; i++) {
    const option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};
Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};
Shopify.postLink = function (path, options) {
  options = options || {};
  const method = options['method'] || 'post';
  const params = options['parameters'] || {};
  const form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);
  for (const key in params) {
    const hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};
Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);
  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));
  this.initCountry();
  this.initProvince();
};
Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    const value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    const value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function () {
    const opt = this.countryEl.options[this.countryEl.selectedIndex];
    const provinces = opt && opt.getAttribute('data-provinces')
      ? JSON.parse(opt.getAttribute('data-provinces'))
      : [];

    // vide la liste des provinces
    while (this.provinceEl.firstChild) {
      this.provinceEl.removeChild(this.provinceEl.firstChild);
    }

    if (provinces.length) {
      provinces.forEach(([value, label]) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        this.provinceEl.appendChild(option);
      });
      this.provinceContainer.style.display = '';
    } else {
      this.provinceContainer.style.display = 'none';
    }
  }
};


/* ===============================================================
   MENU DRAWERS
   =============================================================== */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');

    // Si tu n'as pas de bindEvents, garde en commentaire pour éviter une erreur.
    // this.bindEvents?.();

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));

    // Délégué pour les clics (ex: bouton retour)
    this.addEventListener('click', this.onDelegatedClick, true);
  }

  // Gestion générique du bouton retour
  onDelegatedClick = (e) => {
    const back = e.target.closest('[data-menu-back]');
    if (!back) return;

    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // Ferme UNIQUEMENT le sous-menu courant
    const current = back.closest('details[open]');
    if (!current || current === this.mainDetailsToggle) return;

    this.closeSubmenu(current);

    // Focus sur le parent (ou le hamburger si on revient au root)
    const parent = current.parentElement?.closest('details');
    const focusTarget = parent
      ? parent.querySelector('summary')
      : this.mainDetailsToggle.querySelector('summary');
    try { focusTarget?.focus({ preventScroll: true }); } catch(_) {}
  };

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;
    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;
    if (openDetailsElement === this.mainDetailsToggle) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
    } else {
      this.closeSubmenu(openDetailsElement);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => { this.mainDetailsToggle.classList.add('menu-opening'); });
    summaryElement.setAttribute('aria-expanded', 'true');
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');

    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });

    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });

    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) {
      elementToFocus?.setAttribute('aria-expanded', 'false');
    }
  }

  onFocusOut() {
    setTimeout(() => {
      const tooSoon = Date.now() - this._justOpenedSubmenuAt < 200;
      if (
        !tooSoon &&
        this.mainDetailsToggle.hasAttribute('open') &&
        !this.mainDetailsToggle.contains(document.activeElement)
      ) {
        this.closeMenuDrawer(new Event('focusout'), this.mainDetailsToggle.querySelector('summary'));
      }
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  onOverlayClick(event) {
    event.preventDefault();
    this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    if (parentMenuElement) parentMenuElement.classList.remove('submenu-open');

    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;
    const handleAnimation = (time) => {
      if (animationStart === undefined) animationStart = time;
      const elapsed = time - animationStart;
      if (elapsed < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        const parentOpen = detailsElement.closest('details[open]');
        if (parentOpen) {
          trapFocus(parentOpen, detailsElement.querySelector('summary'));
        }
      }
    };
    window.requestAnimationFrame(handleAnimation);
  }
}

class HeaderDrawer extends MenuDrawer {}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
if (!customElements.get('menu-drawer')) {
  customElements.define('menu-drawer', MenuDrawer);
}



/* ===============================================================
   MODALS
   =============================================================== */
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => { if (event.code.toUpperCase() === 'ESCAPE') this.hide(); });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => { if (event.target === this) this.hide(); });
    }
  }
  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    const section = this.closest('.shopify-section');
    if (section?.id) this.dataset.section = section.id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }
  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup && typeof popup.loadContent === 'function') popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]') || this);
    window.pauseAllMedia();
  }
  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
if (!customElements.get('modal-dialog')) {
  customElements.define('modal-dialog', ModalDialog);
}

/* ... additional code truncated for brevity ... */
(() => {
  // Robust close: mirrors Dawn's logic
  function __closeHeaderDrawer(sourceEl) {
    const details = document.getElementById('Details-menu-drawer-container');
    if (!details) return;

    const host = details.closest('header-drawer');
    const breakpoint = host?.dataset.breakpoint || 'tablet';
    const summary = details.querySelector('summary');

    // remove opening state + all submenus
    details.classList.remove('menu-opening');
    details.querySelectorAll('details').forEach(d => {
      d.removeAttribute('open');
      d.classList.remove('menu-opening');
      d.querySelector('summary')?.setAttribute('aria-expanded','false');
    });

    // close root drawer
    details.removeAttribute('open');
    summary?.setAttribute('aria-expanded','false');

    // body & header cleanup
    document.body.classList.remove(`overflow-hidden-${breakpoint}`);
    document.querySelector('.section-header')?.classList.remove('menu-open');

    // release focus trap (if present)
    if (typeof window.removeTrapFocus === 'function') {
      window.removeTrapFocus(summary || null);
    }

    // return focus to the hamburger if it exists
    try { summary?.focus({ preventScroll: true }); } catch(e) {}
  }

  // Expose globally so inline handlers can call it
  window.__closeHeaderDrawer = __closeHeaderDrawer;

  // Close on overlay, hard-close, menu links, and second click on the hamburger
  document.addEventListener('click', (e) => {
    const t = e.target;

    if (t.closest('.menu-drawer__overlay') || t.closest('.menu-drawer__hard-close')) {
      e.preventDefault();
      __closeHeaderDrawer(t);
      return;
    }



    // Allow toggle-close by clicking the hamburger again while open (mobile)
    if (t.closest('summary.header__icon--menu')) {
      const details = document.getElementById('Details-menu-drawer-container');
      if (details?.hasAttribute('open')) {
        e.preventDefault();
        __closeHeaderDrawer(t);
      }
    }
  }, { capture: true });
})();
// remplace ton debounce actuel par ceci

